import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { FilePlus, FolderPlus, Upload, Edit3, Trash2, Copy, Files, History, ChevronDown } from 'lucide-react'
import './ContextMenu.css'

export type ContextTargetType = 'file' | 'dir' | 'empty'
export type ContextAction = 'rename' | 'duplicate' | 'moveTo' | 'copyPath' | 'fileHistory' | 'delete' | 'newFile' | 'newFolder' | 'upload' | string

export interface MenuItem {
    icon?: ReactNode
    label: string
    action: ContextAction
    danger?: boolean
    submenu?: MenuEntry[]  // kept for API compat but unused in new design
}

export type MenuEntry = MenuItem | 'separator'

/** A collapsible group of menu items with an optional header */
export interface MenuSection {
    /** Section header text. If omitted the items render without a header. */
    title?: string
    /** Section icon shown next to the header */
    icon?: ReactNode
    /** Items in this section */
    items: MenuEntry[]
    /** Start collapsed? Default false (expanded) */
    defaultCollapsed?: boolean
}

interface ContextMenuProps {
    x: number
    y: number
    targetType: ContextTargetType
    targetPath: string | null
    canWrite?: boolean
    /** Flat items (backwards compat) — prefer sections for collapsible groups */
    items?: MenuEntry[]
    /** Sectioned items — overrides items */
    sections?: MenuSection[]
    onAction: (action: ContextAction, path: string | null, type: ContextTargetType) => void
    onClose: () => void
}

function isMobile() {
    return window.matchMedia('(max-width: 600px)').matches || 'ontouchstart' in window
}

/**
 * Premium context menu / action sheet.
 * Desktop: positioned glass dropdown.
 * Mobile: bottom sheet with drag handle + backdrop.
 */
export default function ContextMenu({ x, y, targetType, targetPath, canWrite = true, items, sections, onAction, onClose }: ContextMenuProps) {
    const ref = useRef<HTMLDivElement>(null)
    const sheetRef = useRef<HTMLDivElement>(null)
    const [mobile] = useState(isMobile)
    const [visible, setVisible] = useState(false)
    const [closing, setClosing] = useState(false)

    // Animate in on mount
    useEffect(() => {
        requestAnimationFrame(() => setVisible(true))
    }, [])

    // Close with animation
    const animateClose = useCallback(() => {
        setClosing(true)
        setTimeout(onClose, 200)
    }, [onClose])

    // Click outside / Escape
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) animateClose()
        }
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') animateClose()
        }
        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
        }
    }, [animateClose])

    // Desktop: adjust position to stay in viewport
    useEffect(() => {
        if (mobile || !ref.current) return
        const rect = ref.current.getBoundingClientRect()
        if (rect.right > window.innerWidth) {
            ref.current.style.left = `${x - rect.width}px`
        }
        if (rect.bottom > window.innerHeight) {
            ref.current.style.top = `${y - rect.height}px`
        }
    }, [x, y, mobile, visible])

    // Mobile swipe-to-dismiss
    useEffect(() => {
        if (!mobile || !sheetRef.current) return
        const el = sheetRef.current
        let startY = 0
        let currentY = 0
        let isDragging = false

        function onTouchStart(e: TouchEvent) {
            startY = e.touches[0].clientY
            currentY = startY
            isDragging = true
            el.style.transition = 'none'
        }
        function onTouchMove(e: TouchEvent) {
            if (!isDragging) return
            currentY = e.touches[0].clientY
            const delta = Math.max(0, currentY - startY)
            el.style.transform = `translateY(${delta}px)`
        }
        function onTouchEnd() {
            if (!isDragging) return
            isDragging = false
            el.style.transition = ''
            const delta = currentY - startY
            if (delta > 80) {
                animateClose()
            } else {
                el.style.transform = ''
            }
        }

        el.addEventListener('touchstart', onTouchStart, { passive: true })
        el.addEventListener('touchmove', onTouchMove, { passive: true })
        el.addEventListener('touchend', onTouchEnd, { passive: true })
        return () => {
            el.removeEventListener('touchstart', onTouchStart)
            el.removeEventListener('touchmove', onTouchMove)
            el.removeEventListener('touchend', onTouchEnd)
        }
    }, [mobile, animateClose])

    const act = useCallback((action: ContextAction) => {
        onAction(action, targetPath, targetType)
        animateClose()
    }, [onAction, targetPath, targetType, animateClose])

    // ── Determine what to render ───────────────────────
    // If sections provided, use those. Otherwise convert flat items → single section.
    const resolvedSections: MenuSection[] = sections
        ? sections
        : [{ items: items || buildDefaultItems(targetType, canWrite) }]

    // ── Section component ──────────────────────────────
    function SectionBlock({ section }: { section: MenuSection }) {
        const [collapsed, setCollapsed] = useState(section.defaultCollapsed ?? false)
        const hasHeader = !!section.title
        const contentRef = useRef<HTMLDivElement>(null)

        return (
            <div className="ctx-section">
                {hasHeader && (
                    <button
                        className="ctx-section-header"
                        onClick={() => setCollapsed(!collapsed)}
                        aria-expanded={!collapsed}
                    >
                        {section.icon && <span className="ctx-section-icon">{section.icon}</span>}
                        <span className="ctx-section-title">{section.title}</span>
                        <ChevronDown size={14} className={`ctx-chevron ${collapsed ? 'collapsed' : ''}`} />
                    </button>
                )}
                <div
                    ref={contentRef}
                    className={`ctx-section-body ${hasHeader && collapsed ? 'collapsed' : ''}`}
                    style={hasHeader && collapsed ? { maxHeight: 0, opacity: 0 } : undefined}
                >
                    {section.items.map((item, i) => {
                        if (item === 'separator') return <div key={`sep-${i}`} className="ctx-sep" />
                        return (
                            <button
                                key={`${item.action}-${i}`}
                                className={`ctx-item ${item.danger ? 'danger' : ''}`}
                                onClick={(e) => { e.stopPropagation(); act(item.action) }}
                                role="menuitem"
                            >
                                {item.icon && <span className="ctx-item-icon">{item.icon}</span>}
                                <span className="ctx-item-label">{item.label}</span>
                            </button>
                        )
                    })}
                </div>
            </div>
        )
    }

    const menuContent = (
        <>
            {resolvedSections.map((sec, i) => (
                <SectionBlock key={sec.title || `sec-${i}`} section={sec} />
            ))}
        </>
    )

    // ── Mobile bottom sheet ────────────────────────────
    if (mobile) {
        return (
            <div ref={ref} className={`ctx-overlay ${visible && !closing ? 'visible' : ''} ${closing ? 'closing' : ''}`}>
                <div ref={sheetRef} className="ctx-sheet" role="menu" aria-label="Context menu">
                    <div className="ctx-handle" />
                    <div className="ctx-sheet-scroll">{menuContent}</div>
                </div>
            </div>
        )
    }

    // ── Desktop dropdown ───────────────────────────────
    return (
        <div
            ref={ref}
            className={`ctx-dropdown ${visible && !closing ? 'visible' : ''} ${closing ? 'closing' : ''}`}
            style={{ top: y, left: x }}
            role="menu"
            aria-label="Context menu"
        >
            {menuContent}
        </div>
    )
}

// ── Default items fallback ─────────────────────────────
function buildDefaultItems(targetType: ContextTargetType, canWrite: boolean): MenuEntry[] {
    const items: MenuEntry[] = []
    if (targetType === 'file') {
        items.push(
            { icon: <Edit3 size={14} />, label: 'Rename', action: 'rename' },
            { icon: <Copy size={14} />, label: 'Duplicate', action: 'duplicate' },
            { icon: <FolderPlus size={14} />, label: 'Move to...', action: 'moveTo' },
            'separator',
            { icon: <Files size={14} />, label: 'Copy Path', action: 'copyPath' },
            { icon: <History size={14} />, label: 'File History', action: 'fileHistory' }
        )
        if (canWrite) {
            items.push('separator', { icon: <Trash2 size={14} />, label: 'Delete', action: 'delete', danger: true })
        }
    } else if (targetType === 'dir') {
        items.push(
            { icon: <FilePlus size={14} />, label: 'New File', action: 'newFile' },
            { icon: <FolderPlus size={14} />, label: 'New Folder', action: 'newFolder' },
            'separator',
            { icon: <Upload size={14} />, label: 'Upload Files', action: 'upload' },
            'separator',
            { icon: <Edit3 size={14} />, label: 'Rename', action: 'rename' },
            { icon: <Files size={14} />, label: 'Copy Path', action: 'copyPath' }
        )
        if (canWrite) {
            items.push('separator', { icon: <Trash2 size={14} />, label: 'Delete', action: 'delete', danger: true })
        }
    } else {
        items.push(
            { icon: <FilePlus size={14} />, label: 'New File', action: 'newFile' },
            { icon: <FolderPlus size={14} />, label: 'New Folder', action: 'newFolder' },
            'separator',
            { icon: <Upload size={14} />, label: 'Upload Files', action: 'upload' }
        )
    }
    return items
}
